import { useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { cn } from '@/lib/utils'
import { MapPin, Loader2 } from 'lucide-react'

export interface PlaceDetails {
  placeId: string
  name: string
  address: string
  googleMapsUrl: string
  location: {
    lat: number
    lng: number
  }
  phone?: string
  website?: string
  /** Google Places types, e.g. ['restaurant', 'food']. Basic Data — no extra cost. */
  types: string[]
}

interface PlaceSearchProps {
  onPlaceSelect: (place: PlaceDetails) => void
  placeholder?: string
  className?: string
}

export function PlaceSearch({ onPlaceSelect, placeholder, className }: PlaceSearchProps) {
  const { t } = useTranslation()
  const resolvedPlaceholder = placeholder ?? t('locationSearch.searchPlaceholder')
  const inputRef = useRef<HTMLInputElement>(null)
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const placesLib = useMapsLibrary('places')

  const handlePlaceSelect = useCallback(() => {
    if (!autocompleteRef.current) return

    const place = autocompleteRef.current.getPlace()

    if (!place.place_id || !place.geometry?.location) {
      return
    }

    onPlaceSelect({
      placeId: place.place_id,
      name: place.name || '',
      address: place.formatted_address || '',
      googleMapsUrl: place.url || `https://www.google.com/maps/place/?q=place_id:${place.place_id}`,
      location: {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      },
      phone: place.formatted_phone_number,
      website: place.website,
      types: place.types ?? [],
    })
  }, [onPlaceSelect])

  useEffect(() => {
    if (!placesLib || !inputRef.current) return

    autocompleteRef.current = new placesLib.Autocomplete(inputRef.current, {
      types: ['establishment'],
      fields: [
        'place_id',
        'name',
        'formatted_address',
        'geometry',
        'formatted_phone_number',
        'website',
        'url',
        'types',
      ],
    })

    autocompleteRef.current.addListener('place_changed', handlePlaceSelect)

    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current)
      }
    }
  }, [placesLib, handlePlaceSelect])

  return (
    <div className={cn('relative', className)}>
      <input
        ref={inputRef}
        type="text"
        placeholder={resolvedPlaceholder}
        disabled={!placesLib}
        className="w-full h-9 pl-10 pr-3 border border-gray-300 rounded-md text-sm text-gray-900 placeholder:text-gray-400 bg-white outline-none focus:border-primary focus:ring-primary/50 focus:ring-[3px] disabled:opacity-50"
      />
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
      {!placesLib && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
      )}
    </div>
  )
}
